import type { ComplianceStatusResponse, GetFanClubStatusResponse, ShowSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';

export type ShowPlayerGateAction = {
  label: string;
  variant?: 'primary' | 'secondary';
  onClick: () => void;
};

export type ShowPlayerGateContent = {
  visible: boolean;
  title: string;
  hint?: string;
  primaryAction?: ShowPlayerGateAction;
  /** Prefer this for multiple secondaries (get in line, spy, flash spy). */
  secondaryActions?: ShowPlayerGateAction[];
  /** @deprecated use secondaryActions */
  secondaryAction?: ShowPlayerGateAction;
  tone?: 'default' | 'waiting' | 'muted';
  placement?: 'center' | 'bottom';
};

type GateActions = {
  onJoinShow: () => void;
  onJoinSpy: () => void;
  onJoinFlashSpy: () => void;
  onJoinFanClub: () => void;
  onGetInLine: () => void;
};

/**
 * Soft-joined Main Stage VIEWER rows stay on the membership table after a private
 * starts. Only PARTICIPANT / SPY have paid/show floor access on gated types.
 */
export function hasShowFloorAccess(show: ShowSummary): boolean {
  if (!show.isViewer) {
    return false;
  }

  const role = show.viewerRole ?? 'VIEWER';
  if (role === 'PARTICIPANT' || role === 'SPY') {
    return true;
  }

  if (show.type === 'VIP' || show.type === 'EXCLUSIVE' || show.type === 'PEEP') {
    return false;
  }

  if (
    (show.type === 'PARTY' || show.type === 'TICKET') &&
    ((show.access.entryFeeCoins !== null && show.access.entryFeeCoins > 0) ||
      (show.type === 'PARTY' && (show.access.preTipCoins ?? 0) > 0))
  ) {
    return false;
  }

  return true;
}

/** Fan is in a paid/gated show and should see End show instead of Request private. */
export function isFanInShowSession(show: ShowSummary): boolean {
  return hasShowFloorAccess(show) && show.type !== 'MAIN_STAGE';
}

/**
 * Seated in the current live show (not leftover Main Stage lurkers on a private).
 * Peep booths use VIEWER; VIP/Exclusive require PARTICIPANT or SPY.
 */
export function isInCurrentShow(show: ShowSummary): boolean {
  if (!show.isViewer) {
    return false;
  }

  if (show.type === 'PEEP') {
    const role = show.viewerRole ?? 'VIEWER';
    return role === 'VIEWER' || role === 'PARTICIPANT';
  }

  return hasShowFloorAccess(show);
}

/**
 * Watching a live/waiting non-public floor without a seat.
 * Games, toys, and jukebox belong to people in the show — outsiders only tip + get in line.
 */
export function isOutsideActiveShow(show: ShowSummary): boolean {
  if (show.status !== 'LIVE' && show.status !== 'WAITING') {
    return false;
  }
  if (show.type === 'MAIN_STAGE') {
    return false;
  }
  return !isInCurrentShow(show);
}

export type PrivateShowPanelMode = 'session' | 'queue-modal' | 'queue-bar' | 'hidden';

/** How the private-request chrome should render for this fan. */
export function resolvePrivateShowPanelMode(show: ShowSummary): PrivateShowPanelMode {
  if (isFanInShowSession(show)) {
    return 'session';
  }
  if (show.status === 'ENDED') {
    return 'hidden';
  }
  if (show.type === 'VIP' || show.type === 'EXCLUSIVE') {
    return 'queue-modal';
  }
  if (show.status === 'LIVE' || show.status === 'WAITING' || show.status === 'PAUSED' || show.status === 'AWAY') {
    return 'queue-bar';
  }
  return 'hidden';
}

/** Solo VIP participant with no spies — can pay to take the room Exclusive. */
export function canUpgradeVipToExclusive(show: ShowSummary): boolean {
  return (
    show.type === 'VIP' &&
    show.status === 'LIVE' &&
    show.viewerRole === 'PARTICIPANT' &&
    hasShowFloorAccess(show) &&
    show.access.participantCount <= 1 &&
    (show.spyCount ?? 0) === 0
  );
}

/**
 * Floors that must not auto-join. A leftover Main Stage `isJoining` spinner
 * must not replace Exclusive/VIP/Peep/paid Party/Ticket actions.
 */
export function isConfirmBeforeChargeFloor(show: ShowSummary): boolean {
  if (show.type === 'PEEP' || show.type === 'VIP' || show.type === 'EXCLUSIVE') {
    return true;
  }

  if (
    (show.type === 'PARTY' || show.type === 'TICKET') &&
    ((show.access.entryFeeCoins !== null && show.access.entryFeeCoins > 0) ||
      (show.type === 'PARTY' && (show.access.preTipCoins ?? 0) > 0))
  ) {
    return true;
  }

  return false;
}

function pretipCoins(show: ShowSummary): number {
  return Math.max(0, show.access.preTipCoins ?? 0);
}

function joinVipLabel(show: ShowSummary): string {
  const rate = show.access.coinsPerMinute;
  const pretip = pretipCoins(show);
  if (pretip > 0 && rate) {
    return t('common.show.joinVipPretip')
      .replace('{{pretip}}', String(pretip))
      .replace('{{amount}}', String(rate));
  }
  if (rate) {
    return t('common.show.joinVip').replace('{{amount}}', String(rate));
  }
  return t('common.show.joinGateJoinButton');
}

function joinPartyLabel(show: ShowSummary): string {
  const entry = show.access.entryFeeCoins ?? 0;
  const pretip = pretipCoins(show);
  if (pretip > 0) {
    return t('common.show.joinPartyPretip')
      .replace('{{amount}}', String(entry))
      .replace('{{pretip}}', String(pretip));
  }
  return t('common.show.joinParty').replace('{{amount}}', String(entry));
}

function buildGetInLineAction(actions: GateActions): ShowPlayerGateAction {
  return {
    label: t('common.show.joinGateGetInLineButton'),
    variant: 'secondary',
    onClick: actions.onGetInLine,
  };
}

function buildSpyActions(
  show: ShowSummary,
  actions: GateActions,
): ShowPlayerGateAction[] {
  // Spy never on Exclusive (true private).
  if (show.type === 'EXCLUSIVE') {
    return [];
  }

  // Peep is already watch-only — flash spy (1-min) only, no ongoing spy.
  if (show.type === 'PEEP') {
    if (
      !show.access.flashSpyEnabled ||
      show.access.flashSpyCoins === null ||
      show.access.flashSpyCoins <= 0
    ) {
      return [];
    }
    return [
      {
        label: t('common.show.joinFlashSpy').replace(
          '{{amount}}',
          String(show.access.flashSpyCoins),
        ),
        variant: 'secondary',
        onClick: actions.onJoinFlashSpy,
      },
    ];
  }

  if (!show.access.spyEnabled) {
    return [];
  }

  const result: ShowPlayerGateAction[] = [];

  if (show.access.flashSpyEnabled && show.access.flashSpyCoins !== null && show.access.flashSpyCoins > 0) {
    result.push({
      label: t('common.show.joinFlashSpy').replace('{{amount}}', String(show.access.flashSpyCoins)),
      variant: 'secondary',
      onClick: actions.onJoinFlashSpy,
    });
  }

  if (show.access.spyCoinsPerMinute !== null && show.access.spyCoinsPerMinute > 0) {
    result.push({
      label: t('common.show.joinSpy').replace('{{amount}}', String(show.access.spyCoinsPerMinute)),
      variant: 'secondary',
      onClick: actions.onJoinSpy,
    });
  }

  return result;
}

/** Primary join/ticket/peep + secondary get-in-line + spy options. */
function buildJoinableLiveGate(input: {
  show: ShowSummary;
  title: string;
  hint?: string;
  primary: ShowPlayerGateAction;
  actions: GateActions;
  placement?: 'center' | 'bottom';
}): ShowPlayerGateContent {
  return {
    visible: true,
    placement: input.placement ?? 'bottom',
    title: input.title,
    hint: input.hint,
    primaryAction: input.primary,
    secondaryActions: [buildGetInLineAction(input.actions), ...buildSpyActions(input.show, input.actions)],
  };
}

export function resolveShowPlayerGate(input: {
  show: ShowSummary;
  user: { id: string } | null;
  fanClub: GetFanClubStatusResponse | null;
  isJoining: boolean;
  compliance: ComplianceStatusResponse | null;
  countdownLabel: string | null;
  actions: GateActions;
}): ShowPlayerGateContent {
  const { show, user, fanClub, isJoining, compliance, countdownLabel, actions } = input;
  const hidden: ShowPlayerGateContent = { visible: false, title: '' };

  if (show.status === 'ENDED') {
    return hidden;
  }

  // Break keeps the rotating promo overlay; bottom-left request actions stay available.
  if (show.status === 'PAUSED') {
    return {
      visible: true,
      placement: 'bottom',
      tone: 'muted',
      title: t('common.show.joinGateBreakTitle'),
      hint: t('common.show.joinGateBreakHint'),
      primaryAction: {
        ...buildGetInLineAction(actions),
        variant: 'primary',
      },
    };
  }

  if (show.status === 'AWAY') {
    return {
      visible: true,
      placement: 'bottom',
      tone: 'muted',
      title: t('common.show.joinGateAwayTitle'),
      hint: t('common.show.joinGateAwayHint'),
      primaryAction: {
        ...buildGetInLineAction(actions),
        variant: 'primary',
      },
    };
  }

  // Soft room membership is not enough for paid private floors.
  if (show.status === 'LIVE' && hasShowFloorAccess(show)) {
    return hidden;
  }

  if (!user && show.status === 'LIVE' && show.type === 'MAIN_STAGE') {
    return hidden;
  }

  if (compliance && !compliance.canAccess) {
    if (compliance.action === 'attest') {
      return {
        visible: true,
        title: t('common.show.joinGateAttestTitle'),
        hint: t('common.show.joinGateAttestHint'),
        primaryAction: {
          label: t('common.show.joinGateAttestButton'),
          onClick: () => {
            window.location.href = '/verify';
          },
        },
      };
    }

    if (compliance.action === 'denied') {
      return {
        visible: true,
        tone: 'muted',
        title: t('common.show.joinGateDeniedTitle'),
        hint: t('common.show.joinGateDeniedHint'),
      };
    }

    return {
      visible: true,
      title: t('common.show.joinGateVerifyTitle'),
      hint: t('common.show.joinGateVerifyHint'),
      primaryAction: {
        label: t('common.show.joinGateVerifyButton'),
        onClick: () => {
          window.location.href = '/verify';
        },
      },
    };
  }

  if (isJoining && !isConfirmBeforeChargeFloor(show)) {
    return {
      visible: true,
      tone: 'waiting',
      title: t('common.show.joinGateJoiningTitle'),
      hint: t('common.show.joining'),
    };
  }

  const needsFanClub =
    show.access.requiresFanClub && fanClub !== null && !fanClub.isMember;

  // Club (not a member): join club is primary; get in line + spy secondary.
  if (needsFanClub && fanClub) {
    return buildJoinableLiveGate({
      show,
      title: t('common.show.joinGateFanClubTitle'),
      hint: t('common.show.fanClubHint'),
      primary: {
        label: t('common.show.joinFanClub').replace('{{amount}}', String(fanClub.monthlyCoins)),
        onClick: actions.onJoinFanClub,
      },
      actions,
    });
  }

  if (
    show.type === 'PARTY' &&
    !hasShowFloorAccess(show) &&
    ((show.access.entryFeeCoins !== null && show.access.entryFeeCoins > 0) ||
      (show.access.preTipCoins ?? 0) > 0)
  ) {
    return buildJoinableLiveGate({
      show,
      placement: 'bottom',
      title:
        show.status === 'LIVE'
          ? t('common.show.joinGatePartyLiveTitle')
          : t('common.show.joinGatePartyWaitingTitle'),
      hint:
        show.status === 'WAITING' && show.access.minParticipants
          ? t('common.show.waitingForParticipants')
              .replace('{{current}}', String(show.access.paidParticipantCount))
              .replace('{{required}}', String(show.access.minParticipants))
          : t('common.show.joinGatePartyHint'),
      primary: {
        label: joinPartyLabel(show),
        onClick: actions.onJoinShow,
      },
      actions,
    });
  }

  // Ticket — waiting or live: get your ticket is primary.
  if (show.type === 'TICKET' && !hasShowFloorAccess(show)) {
    const soldOut =
      show.access.ticketCapacity !== null &&
      show.access.ticketsSold >= show.access.ticketCapacity;

    if (soldOut) {
      return {
        visible: true,
        placement: 'bottom',
        tone: 'muted',
        title: t('common.show.soldOut'),
        hint: t('common.show.joinGateTicketSoldOutHint'),
        secondaryActions: [buildGetInLineAction(actions)],
      };
    }

    const hint =
      show.status === 'WAITING'
        ? countdownLabel
          ? t('common.show.scheduledStartsIn').replace('{{time}}', countdownLabel)
          : show.access.scheduledAt
            ? t('common.show.scheduledStartsAt').replace(
                '{{datetime}}',
                new Date(show.access.scheduledAt).toLocaleString(),
              )
            : t('common.show.joinGateTicketLiveHint')
        : t('common.show.joinGateTicketLiveHint');

    return buildJoinableLiveGate({
      show,
      placement: 'bottom',
      title: t('common.show.joinGateTicketTitle'),
      hint,
      primary: {
        label: t('common.show.buyTicket').replace(
          '{{amount}}',
          String(show.access.entryFeeCoins ?? 0),
        ),
        onClick: actions.onJoinShow,
      },
      actions,
    });
  }

  if (hasShowFloorAccess(show) && show.status === 'WAITING' && show.type === 'PARTY') {
    return {
      visible: true,
      placement: 'bottom',
      tone: 'waiting',
      title: t('common.show.joinGateWaitingPartyTitle'),
      hint:
        show.access.minParticipants &&
        show.access.paidParticipantCount < show.access.minParticipants
          ? t('common.show.waitingForParticipants')
              .replace('{{current}}', String(show.access.paidParticipantCount))
              .replace('{{required}}', String(show.access.minParticipants))
          : t('common.show.partyEntryPaid'),
    };
  }

  if (hasShowFloorAccess(show) && show.status === 'WAITING' && show.type === 'TICKET') {
    return {
      visible: true,
      placement: 'bottom',
      tone: 'waiting',
      title: t('common.show.joinGateWaitingTicketTitle'),
      hint: countdownLabel
        ? t('common.show.scheduledStartsIn').replace('{{time}}', countdownLabel)
        : t('common.show.ticketPurchased'),
    };
  }

  // Peep show — join booth primary; get in line + flash spy secondary.
  if (show.type === 'PEEP' && show.status === 'LIVE' && !hasShowFloorAccess(show)) {
    const rate = show.access.coinsPerMinute;
    return buildJoinableLiveGate({
      show,
      title: t('common.show.joinGatePeepTitle'),
      hint: t('common.show.peepMode'),
      primary: {
        label: t('common.show.joinPeep').replace('{{amount}}', String(rate ?? 0)),
        onClick: actions.onJoinShow,
      },
      actions,
    });
  }

  // Club member (or club without membership gate resolved) — join primary.
  if (show.type === 'CLUB' && show.status === 'LIVE' && !hasShowFloorAccess(show)) {
    return buildJoinableLiveGate({
      show,
      title: t('common.show.joinGateClubTitle'),
      hint: t('common.show.joinGateClubHint'),
      primary: {
        label: t('common.show.joinGateJoinButton'),
        onClick: actions.onJoinShow,
      },
      actions,
    });
  }

  if (show.status === 'LIVE' && !hasShowFloorAccess(show)) {
    // VIP — multi-user private: anyone can join by default; get in line for next.
    if (show.type === 'VIP') {
      const rate = show.access.coinsPerMinute;
      const pretip = pretipCoins(show);
      return {
        visible: true,
        placement: 'bottom',
        title: t('common.show.joinGateVipTitle'),
        hint:
          pretip > 0 && rate
            ? t('common.show.joinGateVipHintPretip')
                .replace('{{pretip}}', String(pretip))
                .replace('{{amount}}', String(rate))
            : rate
              ? t('common.show.joinGateVipHint').replace('{{amount}}', String(rate))
              : t('common.show.joinGateVipHintOpen'),
        primaryAction: {
          label: joinVipLabel(show),
          onClick: actions.onJoinShow,
        },
        secondaryActions: [buildGetInLineAction(actions), ...buildSpyActions(show, actions)],
      };
    }

    // Exclusive — true 1-on-1, no spy: get in line only.
    if (show.type === 'EXCLUSIVE') {
      const rate = show.access.coinsPerMinute;
      const pretip = pretipCoins(show);
      return {
        visible: true,
        placement: 'bottom',
        title: t('common.show.joinGateExclusiveTitle'),
        hint:
          pretip > 0 && rate
            ? t('common.show.joinGateExclusiveHintPretip')
                .replace('{{pretip}}', String(pretip))
                .replace('{{amount}}', String(rate))
            : rate
              ? t('common.show.joinGateExclusiveHintRate').replace('{{amount}}', String(rate))
              : t('common.show.joinGateExclusiveHint'),
        primaryAction: {
          ...buildGetInLineAction(actions),
          variant: 'primary',
        },
      };
    }

    return buildJoinableLiveGate({
      show,
      title: t('common.show.joinGateJoinTitle'),
      hint: t('common.show.joinGateJoinHint'),
      primary: {
        label: t('common.show.joinGateJoinButton'),
        onClick: actions.onJoinShow,
      },
      actions,
    });
  }

  return hidden;
}
